import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Breadcrumb,
  Space,
  Button,
  Drawer,
  Statistic,
  Spin,
  Tag,
  Typography,
  Select,
  Input,
  theme,
} from "antd";
import {
  HomeOutlined,
  EyeOutlined,
  SafetyCertificateOutlined,
  DollarCircleOutlined,
  ShoppingOutlined,
  LineChartOutlined,
  SearchOutlined,
  ReloadOutlined,
  AppstoreOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import { Line, Column } from "@ant-design/charts";
import { apiRequest } from "../../../../service/apiRequest";
import { showError } from "../../../../components/errorHandler";
import { HPO } from "../../../../types/HPO";
import { isPlyAndMdfCategory } from "../inventory-managements/PlyAndMdfInventory";

const { Title, Text } = Typography;

// Struct to represent calculated metrics per Ply & MDF supplier
interface PlyMdfSupplierHealth {
  ledger_id: number;
  company_name: string;
  gstin: string;
  total_bills: number;
  total_sheets: number;
  total_spend: number;
  avg_bill_value: number;
  avg_sheet_rate: number;
  varieties_supplied: string[];
  brands_supplied: string[];
  rank?: number;
  bills_list: PlyMdfBillWithCalculated[];
}

export interface PlyMdfBillWithCalculated extends HPO {
  calculated_sheets: number;
  calculated_amount: number;
  tax_amount: number;
  avg_rate: number;
  variety_names: string[];
  brand_names: string[];
  items_breakdown: any[];
}

const PlyMdfSupplierHealthReport: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [purchaseBills, setPurchaseBills] = useState<HPO[]>([]);
  const [productsList, setProductsList] = useState<any[]>([]);
  const [categoriesList, setCategoriesList] = useState<any[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<PlyMdfSupplierHealth | null>(null);
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [drawerVarietyFilter, setDrawerVarietyFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [varietyFilter, setVarietyFilter] = useState<string | null>(null);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [billsData, prodsRes, catsRes] = await Promise.all([
        apiRequest<HPO[]>({
          method: "get",
          url: "get-po-bill",
        }),
        apiRequest<any>({
          method: "get",
          url: "/get-handicraft-products?limit=1000",
        }).catch(() => ({ data: [] })),
        apiRequest<any[]>({
          method: "get",
          url: "category?status=1",
        }).catch(() => []),
      ]);

      setPurchaseBills(billsData || []);
      const prods = prodsRes?.data || (Array.isArray(prodsRes) ? prodsRes : []);
      setProductsList(prods);
      setCategoriesList(catsRes || []);
    } catch (err) {
      console.error(err);
      showError("Failed to fetch Ply & MDF purchase data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Products lookup map
  const productsMap = useMemo(() => {
    const map = new Map<number, any>();
    productsList.forEach((p) => {
      if (p.product_id) {
        map.set(Number(p.product_id), p);
      }
    });
    return map;
  }, [productsList]);

  // Categories lookup map
  const categoriesMap = useMemo(() => {
    const map = new Map<number, string>();
    categoriesList.forEach((c) => {
      if (c.category_id) {
        map.set(Number(c.category_id), c.category_name);
      }
    });
    return map;
  }, [categoriesList]);

  // Helper to test whether a given product/item qualifies as Ply and MDF
  const checkIsPlyAndMdf = (item: any, billType?: string | null): boolean => {
    // 1. Check bill type first
    if (isPlyAndMdfCategory(billType)) {
      return true;
    }

    // 2. Check item category directly if present
    if (isPlyAndMdfCategory(item.category_name)) {
      return true;
    }

    // 3. Lookup product from products master
    const prodId = Number(item.pbg_description || item.product_id);
    if (productsMap.has(prodId)) {
      const pObj = productsMap.get(prodId);
      if (isPlyAndMdfCategory(pObj.category_name)) {
        return true;
      }
      if (pObj.product_category && categoriesMap.has(Number(pObj.product_category))) {
        const catName = categoriesMap.get(Number(pObj.product_category));
        if (isPlyAndMdfCategory(catName)) {
          return true;
        }
      }
    }

    return false;
  };

  // Compute calculated metrics for each bill & group them by supplier (ledger)
  const supplierHealthData = useMemo(() => {
    const suppliersMap = new Map<number, PlyMdfBillWithCalculated[]>();

    purchaseBills.forEach((bill) => {
      // Exclude raw timber logs (handled separately in Timber Supplier Health)
      if (bill.pbill_type && String(bill.pbill_type).toLowerCase() === "timber") {
        return;
      }

      let billSheets = 0;
      let billAmount = 0;
      let billTax = 0;
      const varieties = new Set<string>();
      const brands = new Set<string>();
      const itemsList: any[] = [];

      if (bill.po_details && bill.po_details.length > 0) {
        bill.po_details.forEach((item: any) => {
          // Strictly filter only items where product category is Ply and MDF
          if (!checkIsPlyAndMdf(item, bill.pbill_type)) {
            return;
          }

          const qty = Number(item.pbg_qty || item.qty || item.quantity || 0);
          const rate = Number(item.pbg_rate || item.rate || item.price || 0);
          const itemAmt = qty * rate;
          const cgst = Number(item.pbg_cgst || 0);
          const sgst = Number(item.pbg_sgst || 0);
          const gst = Number(item.pbg_gst || 0);
          const itemTax = cgst + sgst + gst;

          billSheets += qty;
          billAmount += itemAmt;
          billTax += itemTax;

          let itemName = item.product_name || item.item_name || "";
          let brandName = item.brand_name || "Standard";
          let unitName = item.ut_name || item.unit_name || "Sheets";

          const prodId = Number(item.pbg_description || item.product_id);
          if (productsMap.has(prodId)) {
            const pObj = productsMap.get(prodId);
            if (!itemName && pObj.product_name) itemName = pObj.product_name;
            if (pObj.brand_name) brandName = pObj.brand_name;
            if (pObj.ut_name) unitName = pObj.ut_name;
          }

          if (!itemName) {
            itemName = `Ply/MDF Sheet #${item.pbg_id || "Sheet"}`;
          }

          varieties.add(itemName);
          if (brandName && brandName !== "Standard") {
            brands.add(brandName);
          }

          itemsList.push({
            id: item.pbg_id || Math.random(),
            product_name: itemName,
            brand_name: brandName,
            qty,
            unit: unitName,
            rate,
            tax: itemTax,
            amount: itemAmt,
          });
        });
      }

      // If the bill contains NO Ply & MDF items, skip it
      if (itemsList.length === 0) {
        return;
      }

      const finalAmount = billAmount > 0 ? billAmount : Number(bill.pbill_grand_total || 0);
      const avgRate = billSheets > 0 ? finalAmount / billSheets : 0;

      const billWithCalculated: PlyMdfBillWithCalculated = {
        ...bill,
        calculated_sheets: parseFloat(billSheets.toFixed(2)),
        calculated_amount: parseFloat(finalAmount.toFixed(2)),
        tax_amount: parseFloat(billTax.toFixed(2)),
        avg_rate: parseFloat(avgRate.toFixed(2)),
        variety_names: Array.from(varieties).filter(Boolean),
        brand_names: Array.from(brands).filter(Boolean),
        items_breakdown: itemsList,
      };

      const ledgerId = bill.pbill_ledger_id;
      if (!suppliersMap.has(ledgerId)) {
        suppliersMap.set(ledgerId, []);
      }
      suppliersMap.get(ledgerId)!.push(billWithCalculated);
    });

    const suppliersList: PlyMdfSupplierHealth[] = [];

    suppliersMap.forEach((bills, ledgerId) => {
      const firstBill = bills[0];
      const total_bills = bills.length;

      let total_sheets = 0;
      let total_spend = 0;
      const allVarieties = new Set<string>();
      const allBrands = new Set<string>();

      bills.forEach((b) => {
        total_sheets += b.calculated_sheets;
        total_spend += b.calculated_amount;
        b.variety_names.forEach((v) => allVarieties.add(v));
        b.brand_names.forEach((br) => allBrands.add(br));
      });

      const avg_bill_value = total_bills > 0 ? total_spend / total_bills : 0;
      const avg_sheet_rate = total_sheets > 0 ? total_spend / total_sheets : 0;

      suppliersList.push({
        ledger_id: ledgerId,
        company_name: firstBill.ledger_company_name || "Unknown Supplier",
        gstin: firstBill.ledger_gstin || "—",
        total_bills,
        total_sheets: parseFloat(total_sheets.toFixed(2)),
        total_spend: parseFloat(total_spend.toFixed(2)),
        avg_bill_value: parseFloat(avg_bill_value.toFixed(2)),
        avg_sheet_rate: parseFloat(avg_sheet_rate.toFixed(2)),
        varieties_supplied: Array.from(allVarieties).filter(Boolean),
        brands_supplied: Array.from(allBrands).filter(Boolean),
        bills_list: bills.sort((a, b) => b.pbill_id - a.pbill_id),
      });
    });

    // Ranking algorithm (Highest Spend & Sheet Volume on Top):
    return suppliersList
      .sort((a, b) => {
        if (b.total_spend !== a.total_spend) {
          return b.total_spend - a.total_spend;
        }
        return b.total_sheets - a.total_sheets;
      })
      .map((supplier, index) => ({
        ...supplier,
        rank: index + 1,
      }));
  }, [purchaseBills, productsMap, categoriesMap]);

  // Filtered suppliers based on search query and variety filter
  const filteredSuppliers = useMemo(() => {
    return supplierHealthData.filter((sup) => {
      const matchSearch =
        !searchQuery.trim() ||
        sup.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sup.gstin.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sup.varieties_supplied.some((v) => v.toLowerCase().includes(searchQuery.toLowerCase())) ||
        sup.brands_supplied.some((b) => b.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchVariety =
        !varietyFilter ||
        sup.varieties_supplied.some(
          (v) => v.toLowerCase() === varietyFilter.toLowerCase()
        );

      return matchSearch && matchVariety;
    });
  }, [supplierHealthData, searchQuery, varietyFilter]);

  // Unique varieties across all suppliers for filter dropdown
  const allAvailableVarieties = useMemo(() => {
    const set = new Set<string>();
    supplierHealthData.forEach((s) => {
      s.varieties_supplied.forEach((v) => set.add(v));
    });
    return Array.from(set).filter(Boolean);
  }, [supplierHealthData]);

  const handleOpenDrawer = (record: PlyMdfSupplierHealth) => {
    setSelectedSupplier(record);
    setDrawerVarietyFilter(null);
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setSelectedSupplier(null);
    setDrawerVarietyFilter(null);
  };

  // Filtered bills in Drawer based on drawerVarietyFilter
  const displayedBillsList = useMemo(() => {
    if (!selectedSupplier) return [];
    if (!drawerVarietyFilter) return selectedSupplier.bills_list;

    const filterVal = drawerVarietyFilter.toLowerCase();
    return selectedSupplier.bills_list.filter((b) =>
      b.variety_names.some((v) => v.toLowerCase() === filterVal)
    );
  }, [selectedSupplier, drawerVarietyFilter]);

  // Metrics calculated dynamically for filtered bills in Drawer
  const drawerStats = useMemo(() => {
    if (!selectedSupplier) return null;
    const bills = displayedBillsList;
    const total_bills = bills.length;
    let total_sheets = 0;
    let total_spend = 0;

    bills.forEach((b) => {
      total_sheets += b.calculated_sheets;
      total_spend += b.calculated_amount;
    });

    const avg_bill_value = total_bills > 0 ? total_spend / total_bills : 0;
    const avg_sheet_rate = total_sheets > 0 ? total_spend / total_sheets : 0;

    return {
      total_bills,
      total_sheets,
      total_spend,
      avg_bill_value,
      avg_sheet_rate,
    };
  }, [selectedSupplier, displayedBillsList]);

  // Drawer charts data
  const spendTrendData = useMemo(() => {
    if (!displayedBillsList.length) return [];
    return displayedBillsList
      .map((b) => ({
        bill_no: b.pbill_bill_no || `ID-${b.pbill_id}`,
        amount: b.calculated_amount,
      }))
      .reverse();
  }, [displayedBillsList]);

  const qtyTrendData = useMemo(() => {
    if (!displayedBillsList.length) return [];
    return displayedBillsList
      .map((b) => ({
        bill_no: b.pbill_bill_no || `ID-${b.pbill_id}`,
        sheets: b.calculated_sheets,
      }))
      .reverse();
  }, [displayedBillsList]);

  const priceTrendData = useMemo(() => {
    if (!displayedBillsList.length) return [];
    return displayedBillsList
      .map((b) => ({
        bill_no: b.pbill_bill_no || `ID-${b.pbill_id}`,
        rate: b.avg_rate,
      }))
      .reverse();
  }, [displayedBillsList]);

  // Columns for main table
  const columns = [
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      width: 80,
      align: "center" as const,
      render: (rank: number) => {
        let color = "blue";
        if (rank === 1) color = "gold";
        else if (rank === 2) color = "cyan";
        else if (rank === 3) color = "purple";
        return (
          <Tag color={color} style={{ fontWeight: "bold", fontSize: 13, borderRadius: 6 }}>
            Top {rank}
          </Tag>
        );
      },
    },
    {
      title: "Supplier / Company Name",
      key: "company_name",
      render: (_: any, record: PlyMdfSupplierHealth) => (
        <div>
          <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 15 }}>
            {record.company_name}
          </div>
          <div style={{ fontSize: 11, color: "#64748b" }}>
            GSTIN: <strong>{record.gstin}</strong>
          </div>
        </div>
      ),
    },
    {
      title: "Ply & MDF Varieties Supplied",
      key: "varieties",
      render: (_: any, record: PlyMdfSupplierHealth) => {
        if (!record.varieties_supplied || record.varieties_supplied.length === 0) {
          return <span style={{ color: "#94a3b8" }}>Ply & MDF Sheets</span>;
        }
        return (
          <Space size={[4, 4]} wrap>
            {record.varieties_supplied.slice(0, 4).map((v) => (
              <Tag color="orange" key={v} style={{ borderRadius: 4, fontWeight: 500 }}>
                {v}
              </Tag>
            ))}
            {record.varieties_supplied.length > 4 && (
              <Tag color="default">+{record.varieties_supplied.length - 4} more</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Invoices",
      dataIndex: "total_bills",
      key: "total_bills",
      width: 100,
      align: "center" as const,
      sorter: (a: PlyMdfSupplierHealth, b: PlyMdfSupplierHealth) => a.total_bills - b.total_bills,
      render: (val: number) => <span style={{ fontWeight: 700 }}>{val}</span>,
    },
    {
      title: "Total Sheets",
      dataIndex: "total_sheets",
      key: "total_sheets",
      width: 130,
      align: "right" as const,
      sorter: (a: PlyMdfSupplierHealth, b: PlyMdfSupplierHealth) => a.total_sheets - b.total_sheets,
      render: (sheets: number) => (
        <span style={{ fontWeight: 600, color: "#0f172a" }}>
          {sheets.toLocaleString("en-IN")} Sheets
        </span>
      ),
    },
    {
      title: "Avg Rate / Sheet",
      dataIndex: "avg_sheet_rate",
      key: "avg_sheet_rate",
      width: 140,
      align: "right" as const,
      sorter: (a: PlyMdfSupplierHealth, b: PlyMdfSupplierHealth) => a.avg_sheet_rate - b.avg_sheet_rate,
      render: (rate: number) => (
        <span style={{ fontWeight: 600, color: "#1677ff" }}>
          ₹ {rate.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Total Spend (₹)",
      dataIndex: "total_spend",
      key: "total_spend",
      width: 160,
      align: "right" as const,
      sorter: (a: PlyMdfSupplierHealth, b: PlyMdfSupplierHealth) => a.total_spend - b.total_spend,
      render: (spend: number) => (
        <span style={{ color: "#52c41a", fontWeight: 800, fontSize: 15 }}>
          ₹ {spend.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      align: "center" as const,
      render: (_: any, record: PlyMdfSupplierHealth) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="middle"
          onClick={() => handleOpenDrawer(record)}
          style={{ borderRadius: 6, fontWeight: 600 }}
        >
          View
        </Button>
      ),
    },
  ];

  // Aggregated totals for top statistic cards
  const totalAuditedSuppliers = supplierHealthData.length;
  const overallTotalSpend = supplierHealthData.reduce((sum, s) => sum + s.total_spend, 0);
  const overallTotalSheets = supplierHealthData.reduce((sum, s) => sum + s.total_sheets, 0);
  const overallAvgRate = overallTotalSheets > 0 ? overallTotalSpend / overallTotalSheets : 0;

  return (
    <div style={{ padding: "24px 20px", minHeight: "90vh", backgroundColor: "#f8fafc" }}>
      {/* Breadcrumb Header */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item href="/reports-menu">Reports</Breadcrumb.Item>
        <Breadcrumb.Item>Ply & MDF Supplier Health Report</Breadcrumb.Item>
      </Breadcrumb>

      {/* Header Panel */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, color: "#0f172a", letterSpacing: "-0.5px" }}>
            🪵 Ply & MDF Supplier Health Report
          </h1>
          <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: 14 }}>
            Aggregated supplier ranking, sheet volumes, purchase rates, brand distribution, and procurement history for Plywood, MDF boards, and panel sheets.
          </p>
        </div>
      </div>

      {/* Quick Overview Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13, fontWeight: 600, textTransform: "uppercase" }}>Ply & MDF Suppliers</span>}
              value={totalAuditedSuppliers}
              prefix={<SafetyCertificateOutlined style={{ color: "#1677ff", marginRight: 8 }} />}
              valueStyle={{ fontWeight: 800, color: "#0f172a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13, fontWeight: 600, textTransform: "uppercase" }}>Total Ply & MDF Spend</span>}
              value={overallTotalSpend}
              precision={2}
              prefix={<DollarCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />}
              valueStyle={{ fontWeight: 800, color: "#52c41a" }}
              formatter={(val) => `₹ ${Number(val).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13, fontWeight: 600, textTransform: "uppercase" }}>Total Sheets Supplied</span>}
              value={overallTotalSheets}
              precision={0}
              prefix={<AppstoreOutlined style={{ color: "#f97316", marginRight: 8 }} />}
              valueStyle={{ fontWeight: 800, color: "#f97316" }}
              formatter={(val) => `${Number(val).toLocaleString("en-IN")} Sheets`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13, fontWeight: 600, textTransform: "uppercase" }}>Overall Avg Sheet Rate</span>}
              value={overallAvgRate}
              precision={2}
              prefix={<ShoppingOutlined style={{ color: "#8b5cf6", marginRight: 8 }} />}
              valueStyle={{ fontWeight: 800, color: "#8b5cf6" }}
              formatter={(val) => `₹ ${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            />
          </Card>
        </Col>
      </Row>

      {/* Filter / Search Bar */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} md={12} lg={10}>
            <Input
              size="large"
              placeholder="Search supplier by name, GSTIN, or Ply/MDF variety..."
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              size="large"
              style={{ width: "100%" }}
              placeholder="Filter by Ply / MDF Variety"
              allowClear
              value={varietyFilter}
              onChange={(val) => setVarietyFilter(val || null)}
              options={allAvailableVarieties.map((v) => ({ label: v, value: v }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4} lg={4}>
            <Button
              size="large"
              icon={<ReloadOutlined />}
              onClick={() => {
                setSearchQuery("");
                setVarietyFilter(null);
                fetchAllData();
              }}
              style={{ borderRadius: 8 }}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Main Health Table */}
      <div style={{ background: colorBgContainer, borderRadius: borderRadiusLG, padding: 20, boxShadow: "0 4px 18px rgba(0,0,0,0.02)" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table<PlyMdfSupplierHealth>
            columns={columns}
            dataSource={filteredSuppliers}
            rowKey="ledger_id"
            bordered
            size="middle"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} Ply & MDF suppliers`,
            }}
            locale={{ emptyText: "No suppliers found supplying Ply and MDF products" }}
          />
        )}
      </div>

      {/* Detail Drawer */}
      <Drawer
        title={
          selectedSupplier ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Title level={4} style={{ margin: 0, color: "#0f172a" }}>
                  🪵 Ply & MDF Supplier Health: {selectedSupplier.company_name}
                </Title>
                <Tag color="gold" style={{ fontWeight: 700 }}>
                  Rank #{selectedSupplier.rank}
                </Tag>
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                GSTIN: {selectedSupplier.gstin}
              </Text>
            </div>
          ) : (
            "Supplier Detail"
          )
        }
        width={1000}
        onClose={handleCloseDrawer}
        open={drawerVisible}
        destroyOnClose
        style={{ background: "#f8fafc" }}
      >
        {selectedSupplier && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Top Variety Filter Bar */}
            <Card
              size="small"
              bordered={false}
              style={{
                borderRadius: 10,
                background: "#ffffff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <Space align="center" size="middle">
                  <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 14 }}>
                    <TagsOutlined style={{ marginRight: 6, color: "#f97316" }} /> Filter by Sheet Variety:
                  </span>
                  <Select
                    allowClear
                    showSearch
                    placeholder="All Ply & MDF Sheets (Supplier Total)"
                    style={{ width: 320 }}
                    value={drawerVarietyFilter}
                    onChange={(val) => setDrawerVarietyFilter(val || null)}
                    options={selectedSupplier.varieties_supplied.map((v) => ({ label: v, value: v }))}
                  />
                </Space>

                {drawerVarietyFilter ? (
                  <Tag
                    color="orange"
                    closable
                    onClose={() => setDrawerVarietyFilter(null)}
                    style={{ fontSize: 13, padding: "3px 10px", borderRadius: 6 }}
                  >
                    Active Filter: <strong>{drawerVarietyFilter}</strong>
                  </Tag>
                ) : (
                  <span style={{ fontSize: 12, color: "#64748b" }}>
                    Showing all {selectedSupplier.total_bills} invoices
                  </span>
                )}
              </div>
            </Card>

            {/* Drawer Stat Row */}
            <Row gutter={16}>
              <Col span={6}>
                <Card size="small" bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title="Invoices Count"
                    value={drawerStats?.total_bills ?? selectedSupplier.total_bills}
                    suffix="bills"
                    valueStyle={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title="Total Sheets Supplied"
                    value={drawerStats?.total_sheets ?? selectedSupplier.total_sheets}
                    precision={0}
                    suffix="sheets"
                    valueStyle={{
                      fontWeight: 700,
                      fontSize: 18,
                      color: "#f97316",
                    }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title="Avg Rate / Sheet"
                    value={drawerStats?.avg_sheet_rate ?? selectedSupplier.avg_sheet_rate}
                    precision={2}
                    prefix="₹ "
                    valueStyle={{
                      fontWeight: 700,
                      fontSize: 18,
                      color: "#1677ff",
                    }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title="Total Spend"
                    value={drawerStats?.total_spend ?? selectedSupplier.total_spend}
                    precision={2}
                    prefix="₹ "
                    valueStyle={{
                      fontWeight: 700,
                      fontSize: 18,
                      color: "#52c41a",
                    }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Performance Charts */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Title level={5} style={{ margin: 0, color: "#1e293b" }}>
                <LineChartOutlined style={{ marginRight: 8 }} /> Procurement & Pricing Trends {drawerVarietyFilter && `(${drawerVarietyFilter})`}
              </Title>
              {drawerVarietyFilter && (
                <span style={{ fontSize: 12, color: "#f97316", fontWeight: 600 }}>
                  Showing trends specifically for {drawerVarietyFilter}
                </span>
              )}
            </div>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={8}>
                <Card title="Spend Trend (₹)" size="small" bordered={false} style={{ borderRadius: 8, height: 260 }}>
                  {spendTrendData.length > 0 ? (
                    <Column
                      data={spendTrendData}
                      xField="bill_no"
                      yField="amount"
                      height={180}
                      color="#52c41a"
                      tooltip={{ fields: ["bill_no", "amount"] }}
                      xAxis={{ label: { autoRotate: true } }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>No spend history available.</div>
                  )}
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card title="Sheet Supply Volume Trend" size="small" bordered={false} style={{ borderRadius: 8, height: 260 }}>
                  {qtyTrendData.length > 0 ? (
                    <Column
                      data={qtyTrendData}
                      xField="bill_no"
                      yField="sheets"
                      height={180}
                      color="#f97316"
                      tooltip={{ fields: ["bill_no", "sheets"] }}
                      xAxis={{ label: { autoRotate: true } }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>No sheet history available.</div>
                  )}
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card title="Sheet Rate Trend (₹)" size="small" bordered={false} style={{ borderRadius: 8, height: 260 }}>
                  {priceTrendData.length > 0 ? (
                    <Line
                      data={priceTrendData}
                      xField="bill_no"
                      yField="rate"
                      height={180}
                      smooth
                      color="#1677ff"
                      tooltip={{ fields: ["bill_no", "rate"] }}
                      xAxis={{ label: { autoRotate: true } }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>No rate history available.</div>
                  )}
                </Card>
              </Col>
            </Row>

            {/* Individual Purchase Bills Detail Table */}
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "8px 0 12px 0",
                }}
              >
                <Title level={5} style={{ margin: 0, color: "#1e293b" }}>
                  📋 Individual Ply & MDF Invoices {drawerVarietyFilter && `(${drawerVarietyFilter})`}
                </Title>
                <span style={{ fontSize: 12, color: "#64748b" }}>
                  Showing <strong>{displayedBillsList.length}</strong> {displayedBillsList.length === 1 ? "invoice" : "invoices"}
                </span>
              </div>

              <Table<PlyMdfBillWithCalculated>
                dataSource={displayedBillsList}
                size="small"
                bordered
                pagination={{ defaultPageSize: 5 }}
                rowKey="pbill_id"
                expandable={{
                  expandedRowRender: (record) => (
                    <div style={{ background: "#fff7ed", padding: "12px 16px", borderRadius: 8, border: "1px solid #fed7aa" }}>
                      <div style={{ fontWeight: 700, marginBottom: 8, color: "#9a3412" }}>
                        🪵 Ply & MDF Sheets Breakdown ({record.items_breakdown.length} items):
                      </div>
                      <Table
                        dataSource={record.items_breakdown}
                        rowKey="id"
                        size="small"
                        pagination={false}
                        bordered
                        columns={[
                          {
                            title: "Sheet / Item Name",
                            dataIndex: "product_name",
                            key: "product_name",
                            render: (name: string) => <strong>{name}</strong>,
                          },
                          {
                            title: "Brand",
                            dataIndex: "brand_name",
                            key: "brand_name",
                            render: (brand: string) => <Tag color="orange">{brand}</Tag>,
                          },
                          {
                            title: "Quantity",
                            dataIndex: "qty",
                            key: "qty",
                            align: "right" as const,
                            render: (qty: number, it: any) => `${qty} ${it.unit}`,
                          },
                          {
                            title: "Rate / Sheet (₹)",
                            dataIndex: "rate",
                            key: "rate",
                            align: "right" as const,
                            render: (rate: number) => `₹ ${rate.toFixed(2)}`,
                          },
                          {
                            title: "GST / Tax (₹)",
                            dataIndex: "tax",
                            key: "tax",
                            align: "right" as const,
                            render: (tax: number) => `₹ ${tax.toFixed(2)}`,
                          },
                          {
                            title: "Total Amount (₹)",
                            dataIndex: "amount",
                            key: "amount",
                            align: "right" as const,
                            render: (amt: number) => (
                              <strong style={{ color: "#52c41a" }}>
                                ₹ {amt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </strong>
                            ),
                          },
                        ]}
                      />
                    </div>
                  ),
                  rowExpandable: (record) => record.items_breakdown && record.items_breakdown.length > 0,
                }}
                columns={[
                  {
                    title: "Bill No.",
                    dataIndex: "pbill_bill_no",
                    key: "pbill_bill_no",
                    render: (val: any, record: PlyMdfBillWithCalculated) => (
                      <div>
                        <strong>{val || `ID-${record.pbill_id}`}</strong>
                        {record.pbill_challan_no && (
                          <div style={{ fontSize: 10, color: "#94a3b8" }}>Challan: {record.pbill_challan_no}</div>
                        )}
                      </div>
                    ),
                  },
                  {
                    title: "Bill Date",
                    dataIndex: "pbill_bill_date",
                    key: "pbill_bill_date",
                  },
                  {
                    title: "Sheets & Varieties",
                    key: "varieties",
                    render: (_: any, record: PlyMdfBillWithCalculated) => (
                      <Space size={[2, 4]} wrap>
                        {record.variety_names.map((v) => (
                          <Tag color="orange" key={v} style={{ borderRadius: 4 }}>
                            {v}
                          </Tag>
                        ))}
                      </Space>
                    ),
                  },
                  {
                    title: "Brands",
                    key: "brands",
                    render: (_: any, record: PlyMdfBillWithCalculated) => (
                      <Space size={[2, 4]} wrap>
                        {record.brand_names.map((b) => (
                          <Tag color="geekblue" key={b} style={{ borderRadius: 4 }}>
                            {b}
                          </Tag>
                        ))}
                      </Space>
                    ),
                  },
                  {
                    title: "Total Sheets",
                    dataIndex: "calculated_sheets",
                    key: "calculated_sheets",
                    align: "right" as const,
                    render: (qty: number) => <strong>{qty.toLocaleString("en-IN")}</strong>,
                  },
                  {
                    title: "Avg Rate",
                    dataIndex: "avg_rate",
                    key: "avg_rate",
                    align: "right" as const,
                    render: (rate: number) => `₹ ${rate.toFixed(2)}`,
                  },
                  {
                    title: "Bill Amount (₹)",
                    dataIndex: "calculated_amount",
                    key: "calculated_amount",
                    align: "right" as const,
                    render: (amt: number) => (
                      <span style={{ fontWeight: 700, color: "#52c41a" }}>
                        ₹ {amt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default PlyMdfSupplierHealthReport;
